import { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { UseFormReturn } from "react-hook-form";
import CountdownComponent from "../../../components/Common/Countdown/Countdown";
import { authGetOtpController } from "../../../lib/controllers/auth/otp/get";
import { ErrorMessageComponent } from "../../Common/Input/ErrorMessage/ErrorMessage";
import { PrimeButton } from "../../prime/button";
import { OtpFormSchemaType } from "./OtpFormSchema";
import { PrimeLink } from "../../prime/link";
import { featureFlag } from "../../../lib/featureFlags/featureFlags";

interface AuthOtpComponentProps {
  onSubmit: (data: any) => Promise<void>;
  useFormProps: UseFormReturn<OtpFormSchemaType>;
  isFetching: boolean;
  componentId?: string;
}

const IS_RECAPTCHA_ENABLED = featureFlag("recaptcha")

export const AuthOtpComponent = (props: AuthOtpComponentProps) => {
  const { onSubmit, useFormProps, isFetching } = props;
  const [isCountingDown, setIsCountingDown] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState(isFetching);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const [email, setEmail] = useState<string>("");
  const [action, setAction] = useState<string>("");
  const [currentCountry, setCurrentCountry] = useState<string>("");
  const [signupData, setSignupData] = useState();

  console.log("isCountingDown",isCountingDown )

  const resendOtp = async () => {
    setIsLoading(true);

    let token = "default";
    if (IS_RECAPTCHA_ENABLED) {
      // Get recaptcha token
      if (!recaptchaRef.current) throw "reCaptcha null";
      token = (await recaptchaRef.current.executeAsync()) ?? "";
      if (!token) throw "token null";
    }

    // Request OTP
    const [_, error] = await authGetOtpController(
      email,
      action,
      token,
      currentCountry,
      signupData
    );

    if (error && error.message !== "Not Found") {
      useFormProps.setError(
        "code",
        { type: "custom", message: error.message + "" },
        { shouldFocus: true }
      );
      recaptchaRef.current?.reset();
      return;
    }

    const otpCountdownData = {
      email,
      action,
      currentCountry,
      signupData,
      otpSentAt: new Date().toJSON(),
    };
    localStorage.removeItem("otpCountdownData");
    localStorage.setItem("otpCountdownData", JSON.stringify(otpCountdownData));
    window.location.reload();
  };

  useEffect(() => {
    const storedDataString = localStorage.getItem("otpCountdownData");
    if (storedDataString) {
      const storedData = JSON.parse(storedDataString);
      setEmail(storedData.email);
      setAction(storedData.action);
      setCurrentCountry(storedData.currentCountry);
      setSignupData(storedData.signupData);
    }
  }, []);

  useEffect(() => {
    setIsLoading(isFetching);
  }, [isFetching]);

  const submitForm = (data: any) => {
    setIsLoading(true);
    onSubmit(data);
  };

  return (
    <form
      id={props.componentId}
      onSubmit={useFormProps.handleSubmit(submitForm)}
    >
      <input
        id={props.componentId + "-input-otp"}
        type="text"
        className={`fp-text-input static mt-4 ${
          useFormProps.formState.errors.code?.message && `border-red-600`
        }`}
        placeholder="Enter code here"
        autoComplete="off"
        {...useFormProps.register("code")}
      />

      <ErrorMessageComponent
        componentId={props.componentId + "-error-message"}
        fieldError={useFormProps.formState.errors.code}
      />
      <div className="h-6"></div>
      {IS_RECAPTCHA_ENABLED && (
        <ReCAPTCHA
          ref={recaptchaRef}
          size="invisible"
          sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ""}
          badge="bottomleft"
        />
      )}
      <PrimeButton
        size="large"
        id={props.componentId + "-submit-otp"}
        type="submit"
        status={isFetching ? "loading" : "default"}
        loadingText="Verifying your code..."
      >
        Verify code
      </PrimeButton>

      <div className="mt-4">
        {isCountingDown ? (
          <p className="text-center text-sm font-light text-slate-500">
            Verification code expires in{" "}
            <CountdownComponent
              totalTime={300}
              updateIsCountingDown={() => setIsCountingDown(!isCountingDown)}
            />
          </p>
        ) : (
          <div className="flex text-sm justify-center">
            <p className="font-light text-slate-500 mr-1">
              Verification code expired.
            </p>

            <PrimeLink
              id={props.componentId + "-resend-link"}
              onClick={resendOtp}
              href={"#"}
            >
              Resend OTP
            </PrimeLink>
          </div>
        )}
      </div>
    </form>
  );
};
